import {
  FALLBACK_ACCELERATOR,
  FALLBACK_CONFIG,
  FALLBACK_SERVER,
  buildFallbackCatalog,
} from '@/lib/fallbackCatalog';
import type { CatalogData } from '@/types';

type CatalogQueryResults = {
  accelerator?: CatalogData['accelerator'] | null;
  server?: CatalogData['server'] | null;
  models?: CatalogData['models'] | null | undefined;
  throughput?: CatalogData['throughput'] | null | undefined;
  profiles?: CatalogData['profiles'] | null | undefined;
  config?: CatalogData['config'] | null | undefined;
};

// Sort models deterministically for stable hashing
function sortModels(models: CatalogData['models']): CatalogData['models'] {
  if (!Array.isArray(models)) return models;
  return [...models].sort((a, b) => {
    const idCmp = a.model_id.localeCompare(b.model_id);
    if (idCmp !== 0) return idCmp;
    return (a.params_b ?? 0) - (b.params_b ?? 0);
  });
}

// Sort throughput deterministically for stable hashing
function sortThroughput(throughput: CatalogData['throughput']): CatalogData['throughput'] {
  if (!Array.isArray(throughput)) return throughput;
  return [...throughput].sort((a, b) => {
    const idCmp = a.model_id.localeCompare(b.model_id);
    if (idCmp !== 0) return idCmp;
    const precCmp = a.precision - b.precision;
    if (precCmp !== 0) return precCmp;
    const batchCmp = a.batch - b.batch;
    if (batchCmp !== 0) return batchCmp;
    return a.backend.localeCompare(b.backend);
  });
}

// Sort profiles deterministically for stable hashing
function sortProfiles(profiles: CatalogData['profiles']): CatalogData['profiles'] {
  if (!Array.isArray(profiles)) return profiles;
  return [...profiles].sort((a, b) => a.profile_id.localeCompare(b.profile_id));
}

export function materializeCatalogFromQueryData(
  {
    accelerator,
    server,
    models,
    throughput,
    profiles,
    config,
  }: CatalogQueryResults,
  options?: { preventFallback?: boolean }
) {
  const fallback = buildFallbackCatalog();

  const modelsData = Array.isArray(models) ? models : [];
  const throughputData = Array.isArray(throughput) ? throughput : [];
  const modelsProvided = models !== undefined;
  const throughputProvided = throughput !== undefined;
  const hasModelsData = modelsData.length > 0;
  const hasThroughputData = throughputData.length > 0;

  // If fallback is prevented and we don't have data, throw error
  if (options?.preventFallback && (!hasModelsData || !hasThroughputData)) {
    throw new Error(
      `Catalog materialization failed: models=${hasModelsData}, throughput=${hasThroughputData}. ` +
      `Fallback was prevented (likely during frozen run).`
    );
  }

  const shouldUseFallbackPair = !(hasModelsData && hasThroughputData);

  const shouldLogFallback =
    shouldUseFallbackPair && (modelsProvided || throughputProvided) && (!hasModelsData || !hasThroughputData);

  if (shouldLogFallback) {
    const missingSources: Array<'models' | 'throughput'> = [];
    if (!hasModelsData) missingSources.push('models');
    if (!hasThroughputData) missingSources.push('throughput');

    console.error('[catalog] ⚠️ Missing catalog datasets forced fallback pair', {
      missingSources,
      modelsCount: modelsData.length,
      throughputCount: throughputData.length,
    });
  }

  const resolvedModels = shouldUseFallbackPair ? fallback.models : models ?? fallback.models;
  const resolvedThroughput = shouldUseFallbackPair
    ? fallback.throughput
    : throughput ?? fallback.throughput;

  return {
    catalog: {
      accelerator: accelerator ?? { ...FALLBACK_ACCELERATOR },
      server: server ?? { ...FALLBACK_SERVER },
      models: sortModels(resolvedModels),
      throughput: sortThroughput(resolvedThroughput),
      profiles: sortProfiles(profiles ?? fallback.profiles),
      config: config ?? { ...FALLBACK_CONFIG },
    } satisfies CatalogData,
    usedFallbackPair: shouldUseFallbackPair,
  };
}
